import React, { createContext, forwardRef, useContext } from 'react';
import './appearanceBoundary.css';
import SuspenseLoader from 'src/components/SuspenseLoader';

// Shared through React context so portaled descendants retain the same readiness.
export const AppearancePendingContext = createContext(false);
const AppearanceLoadingContext = createContext(false);

type Props = React.HTMLAttributes<HTMLDivElement> & {
  pending: boolean;
  error?: string;
  onRetry?: () => void;
  retryLabel?: string;
  retainContentOnError?: boolean;
};

/** Keep inputs mounted, but never paint a table before its owning appearance is ready. */
const AppearanceBoundary = forwardRef<HTMLDivElement, Props>(
  function AppearanceBoundary(
    {
      pending,
      error,
      retainContentOnError = false,
      onRetry,
      retryLabel = 'Retry appearance',
      children,
      ...props
    },
    ref
  ) {
    const inheritedPending = useContext(AppearancePendingContext);
    const inheritedLoading = useContext(AppearanceLoadingContext);
    const blocked = inheritedPending || pending || Boolean(error && !retainContentOnError);
    return (
      <AppearancePendingContext.Provider value={blocked}>
      <AppearanceLoadingContext.Provider value={inheritedLoading || pending}>
      <div
        {...props}
        ref={ref}
        data-appearance-pending={blocked ? 'true' : undefined}
        aria-busy={(pending && !error) || undefined}
      >
        {error ? (
          <div className="document-theme-load-error" role="alert">
            {error}
            {onRetry && (
              <button type="button" onClick={onRetry}>
                {retryLabel}
              </button>
            )}
          </div>
        ) : (
          pending && !inheritedLoading && <div className="document-theme-loading appearance-initial-loading" role="status" aria-label="Loading company appearance"><SuspenseLoader /></div>
        )}
        {children}
      </div>
      </AppearanceLoadingContext.Provider>
      </AppearancePendingContext.Provider>
    );
  }
);
export default AppearanceBoundary;
